import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { VariantContextData } from "../variants/VariantBuilderContext";
import RedirectUrlFieldsService from "packs/services/convertflow/elements/redirect_url_fields_service";

/**
 * Shared "+ Field" picker used by redirect URL fields, AI prompt, and HTML editor.
 * Calls onSelect(badge) where badge is { type, attribute?, slug?, key? }.
 */
const FieldPickerPopover = (props) => {
  const contextValue = useContext(VariantContextData);
  const contextData = contextValue?.[0];

  const website = props.website || contextData?.objects?.website;
  const variant = props.variant || contextData?.objects?.variant;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);

  const excludeContactAttributes = props.excludeContactAttributes || [];
  const excludeSlugs = props.excludeSlugs || [];
  const excludeScore = props.excludeScore === true;
  const buttonLabel = props.buttonLabel || "+ Field";
  const buttonClassName =
    props.buttonClassName ||
    "absolute top-[6px] right-2 px-2 py-1 rounded-lg border-none text-lg text-blue-600 dark:text-blue-400 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer whitespace-nowrap";
  const dropdownClassName =
    props.dropdownClassName ||
    "absolute right-0 top-full z-50 mt-1 w-full max-w-[300px] max-h-[300px] overflow-y-auto bg-white dark:bg-slate-950 rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 shadow-lg";

  const funnelFieldsBySlug = {};
  Object.values(variant?.fields || {})
    .filter((field) => field?.toBeDeleted !== true)
    .forEach((field) => {
      if (field.slug && field.slug !== "New Field") {
        funnelFieldsBySlug[field.slug] = field;
      }
    });

  const searchMatch = (label) =>
    !search || label.toLowerCase().includes(search.toLowerCase());

  let contactOptions = [];
  let funnelOptions = [];
  let websiteOptions = [];

  if (open) {
    contactOptions = RedirectUrlFieldsService.contactFields()
      .filter((contact) => !excludeContactAttributes.includes(contact.attribute))
      .filter((contact) =>
        searchMatch(contact.label + " " + contact.attribute)
      );

    funnelOptions = Object.values(funnelFieldsBySlug)
      .filter(
        (field) =>
          field.slug.indexOf("survey_") === -1 &&
          !excludeSlugs.includes(field.slug)
      )
      .sort((a, b) => a.slug.localeCompare(b.slug))
      .filter((field) => searchMatch((field.label || "") + " " + field.slug));

    const funnelSlugs = Object.keys(funnelFieldsBySlug);
    websiteOptions = Object.values(website?.fields || {})
      .filter(
        (field) =>
          field.slug &&
          field.slug !== "New Field" &&
          field.slug.indexOf("survey_") === -1 &&
          !funnelSlugs.includes(field.slug) &&
          !excludeSlugs.includes(field.slug)
      )
      .sort((a, b) => a.slug.localeCompare(b.slug))
      .filter((field) => searchMatch(field.slug));
  }

  useEffect(() => {
    if (!open) return undefined;

    function handleClickOutside(e) {
      const insideRoot = rootRef.current && rootRef.current.contains(e.target);
      if (!insideRoot) {
        setOpen(false);
        setSearch("");
      }
    }

    function handleKeyDown(e) {
      if (e.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [open]);

  const selectBadge = (badge) => {
    if (props.onSelect) {
      props.onSelect(badge);
    }
    setOpen(false);
    setSearch("");
  };

  const dropdownGroup = (title, options) =>
    options.length > 0 && (
      <React.Fragment>
        <div className="px-3 py-2 text-lg text-slate-500 dark:text-slate-400 font-semibold">
          {title}
        </div>
        {options.map((option) => (
          <div
            key={option.key}
            role="option"
            tabIndex={0}
            className="px-3 py-2 text-xl text-black dark:text-white cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 outline-none truncate"
            data-field-token={option.key}
            title={option.title}
            onClick={() => selectBadge(option.badge)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                selectBadge(option.badge);
              }
            }}
          >
            {option.label}
          </div>
        ))}
      </React.Fragment>
    );

  return (
    <div className={props.className || "relative"} ref={rootRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={buttonClassName}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        {buttonLabel}
      </button>

      {open && (
        <div role="listbox" className={dropdownClassName}>
          <div className="p-2">
            <input
              type="text"
              className="w-full p-2 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-lg border-solid border-[1px] border-slate-200 dark:border-slate-700 outline-none focus:outline-none focus:ring-0"
              placeholder="Search fields"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {dropdownGroup(
            "Contact fields",
            contactOptions.map((contact) => ({
              key: "contact-" + contact.attribute,
              label: contact.label,
              badge: {
                key: null,
                type: "contact_field",
                attribute: contact.attribute,
              },
            }))
          )}

          {dropdownGroup(
            "This Funnel's Fields",
            funnelOptions.map((field) => ({
              key: "funnel-" + field.slug,
              label: field.slug,
              title: field.label,
              badge: { key: null, type: "account_field", slug: field.slug },
            }))
          )}

          {!excludeScore &&
            searchMatch("quiz score") &&
            dropdownGroup("Quiz", [
              {
                key: "score",
                label: "Quiz score",
                badge: { key: null, type: "score" },
              },
            ])}

          {dropdownGroup(
            "All fields",
            websiteOptions.map((field) => ({
              key: "slug-" + field.slug,
              label: field.slug,
              badge: { key: null, type: "account_field", slug: field.slug },
            }))
          )}

          {contactOptions.length === 0 &&
            funnelOptions.length === 0 &&
            websiteOptions.length === 0 &&
            (excludeScore || !searchMatch("quiz score")) && (
              <div className="px-3 py-2 text-lg text-slate-500 dark:text-slate-400">
                No fields found
              </div>
            )}
        </div>
      )}
    </div>
  );
};

FieldPickerPopover.propTypes = {
  website: PropTypes.object,
  variant: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  buttonLabel: PropTypes.string,
  buttonClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
  className: PropTypes.string,
  excludeContactAttributes: PropTypes.arrayOf(PropTypes.string),
  excludeSlugs: PropTypes.arrayOf(PropTypes.string),
  excludeScore: PropTypes.bool,
};

export default FieldPickerPopover;

export function badgeToPromptToken(badge) {
  if (!badge) return null;
  if (badge.type === "contact_field") return "{" + badge.attribute + "}";
  if (badge.type === "account_field") return "{" + badge.slug + "}";
  if (badge.type === "score") return "{CURRENT_SCORE}";
  return null;
}

export function badgeToContentMergeTag(badge) {
  if (!badge) return null;
  if (badge.type === "contact_field") {
    const fallback = badge.attribute === "firstname" ? "friend" : "";
    return (
      "({person." + badge.attribute + "} OR {fallback=" + fallback + "})"
    );
  }
  if (badge.type === "account_field") {
    return "({person.fields." + badge.slug + "} OR {fallback=})";
  }
  if (badge.type === "score") return "({CURRENT_SCORE})";
  return RedirectUrlFieldsService.buildToken(badge);
}
